import { useState, useRef, useEffect } from 'react'
import { normalizeText } from '@/lib/search'

interface SearchableSelectOption {
  value: string
  label: string
  meta?: string // Additional info like symbol
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  onAfterSelect?: () => void
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  error = false,
  onAfterSelect
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredOptions, setFilteredOptions] = useState<SearchableSelectOption[]>(options)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null)

  // Filter options based on search query
  useEffect(() => {
    const q = normalizeText(searchQuery)
    const filtered = options.filter((opt) => {
      const l = normalizeText(opt.label || '')
      const v = normalizeText(opt.value || '')
      const m = normalizeText(opt.meta || '')
      return l.includes(q) || v.includes(q) || m.includes(q)
    })
    setFilteredOptions(filtered)
    setHighlightedIndex(-1)
  }, [searchQuery, options])

  // Get the label of the selected value
  const selectedLabel = options.find((opt) => opt.value === value)?.label || ''

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        setIsOpen(true)
        e.preventDefault()
        return
      }

      if (e.key === 'ArrowDown') {
        // On desktop (fine pointer) allow opening the dropdown and highlight first
        const isDesktop =
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(pointer: fine)').matches

        if (isDesktop && filteredOptions.length >= 2) {
          setIsOpen(true)
          setHighlightedIndex(0)
          e.preventDefault()
          return
        }

        // Fallback: just open dropdown
        setIsOpen(true)
        e.preventDefault()
        return
      }

      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        // If there's exactly one filtered option and we're on a desktop, select it on Enter
        if (
          filteredOptions.length === 1 &&
          highlightedIndex === -1 &&
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(pointer: fine)').matches
        ) {
          selectOption(filteredOptions[0])
          break
        }

        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          selectOption(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearchQuery('')
        break
    }
  }

  // Handle option click
  const selectOption = (option: SearchableSelectOption) => {
    onChange(option.value)
    // Close dropdown first, then run after-select to ensure focus goes to the next field
    setIsOpen(false)
    setSearchQuery('')
    setTimeout(() => {
      try {
        toggleButtonRef.current?.blur()
      } catch (_) {
        // ignore
      }
      onAfterSelect?.()
    }, 0)
  }

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchQuery : selectedLabel}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2 rounded-lg border ${
            error ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'
          }`}
        />
        <button
          ref={toggleButtonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-50"
        >
          <svg
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 text-center">
              Nenhuma unidade encontrada
            </div>
          ) : (
            <ul>
              {filteredOptions.map((option, index) => (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => selectOption(option)}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                      index === highlightedIndex
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                        : 'text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-600'
                    }`}
                  >
                    <div className="font-medium">{option.label}</div>
                    {option.meta && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {option.meta}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
